import type { HotelComparator } from "@b2bportal/core-types";
import { HotelSortOptionEnum } from "@b2bportal/core-types";
import {
  hotelListSortComparatorsMap as coreHotelListSortComparatorsMap,
  sortByStarRating,
  toStarRating,
  toHotelStarRating,
  toStarRatingForAvailableRating,
} from "@b2bportal/core-utilities";
import type { Lodging } from "@b2bportal/lodging-api";

/**
 * RBC-specific price sorting that uses nightlyDiscountAwareTaxesAndFeesIncluded
 * to match what's displayed in the UI (price including taxes and fees).
 *
 * This ensures vacation rentals and hotels with different tax rates sort correctly.
 */
export const sortByPrice = (a: Lodging, b: Lodging) => {
  // Use nightlyDiscountAwareTaxesAndFeesIncluded to match what's displayed in the UI
  // This ensures sorting matches the visible price (including taxes and fees)
  // Fall back to nightlyDiscountAware, then totalPrice for compatibility
  const aPrice =
    a.price?.nightlyDiscountAwareTaxesAndFeesIncluded
      ?.priceWithUnmanagedDiscounts?.fiat?.value ??
    a.price?.nightlyDiscountAware?.priceWithUnmanagedDiscounts?.fiat?.value ??
    a.price?.totalPrice?.fiat.value;
  const bPrice =
    b.price?.nightlyDiscountAwareTaxesAndFeesIncluded
      ?.priceWithUnmanagedDiscounts?.fiat?.value ??
    b.price?.nightlyDiscountAware?.priceWithUnmanagedDiscounts?.fiat?.value ??
    b.price?.totalPrice?.fiat.value;
  // If a hotel does not have a price, it's unavailable and should be pushed to the end
  if (aPrice == null && bPrice == null) {
    return 0;
  } else if (aPrice == null) {
    return 1;
  } else if (bPrice == null) {
    return -1;
  } else {
    return aPrice - bPrice;
  }
};

/**
 * RBC-specific user rating sorting that handles null/undefined scores
 */
export const sortByUserRating = (a: Lodging, b: Lodging) => {
  const aScore = a.lodging.score ?? 0;
  const bScore = b.lodging.score ?? 0;
  return bScore - aScore;
};

export const hotelListSortComparatorsMap: Record<
  HotelSortOptionEnum,
  HotelComparator | null
> = {
  ...coreHotelListSortComparatorsMap,
  [HotelSortOptionEnum.Price]: sortByPrice,
  [HotelSortOptionEnum.UserRating]: sortByUserRating,
};

// Re-export everything else from core for convenience
export {
  sortByStarRating,
  toStarRating,
  toHotelStarRating,
  toStarRatingForAvailableRating,
};
